import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { EndpointHint, HelpIcon, LoadFailure, Skeleton, type AdminClient } from '../../../modules/core/index.js'
import { createAiBriefingsAdminApi } from '../adminApi.js'
import { usdLabel } from '../labels.js'
import type { BriefingUsageTotals } from '../types.js'

function Tile({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="rounded border border-marine/15 bg-white px-3 py-2">
      <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">{label}</div>
      <div className="text-lg font-black text-ink">{value}</div>
      {detail && <div className="text-[10px] text-ink-mid">{detail}</div>}
    </div>
  )
}

function TotalsCells({ totals }: { totals: BriefingUsageTotals }) {
  return (
    <>
      <td className="py-1.5 pr-3 text-ink">{totals.briefings}</td>
      <td className="py-1.5 pr-3 text-ink">{totals.questions}</td>
      <td className="py-1.5 pr-3 text-ink">{totals.emails}</td>
      <td className="py-1.5 pr-3 text-ink-mid">{totals.empty}</td>
      <td className={`py-1.5 pr-3 ${totals.failed > 0 ? 'text-coral' : 'text-ink-mid'}`}>{totals.failed}</td>
      <td className="py-1.5 pr-3 text-ink-mid">{totals.refused}</td>
      <td className="py-1.5 pr-3 text-ink-mid">{(totals.inputTokens + totals.outputTokens).toLocaleString('en-US')}</td>
      <td className="py-1.5 text-ink">{usdLabel(totals.costUsd)}</td>
    </>
  )
}

const HEADERS = ['Briefings', 'Questions', 'Emails', 'Empty', 'Failed', 'Refused', 'Tokens', 'Est. cost']

/**
 * System › AI (docs/plans/ai-briefings-plan.md section 13): whether the deployment writes and emails briefings,
 * the model, prices and cap, the last seven days' tallies and the same by client. Counts and estimates only,
 * never a briefing's words.
 */
export function AiUsagePage({ client }: { client: AdminClient }) {
  const api = useMemo(() => createAiBriefingsAdminApi(client), [client])
  const query = useQuery({ queryKey: ['admin', 'ai', 'overview'], queryFn: () => api.overview() })
  if (query.isPending) {
    return <Skeleton lines={6} />
  }
  if (query.isError) {
    return <LoadFailure message="Could not read the AI usage." onRetry={() => void query.refetch()} retrying={query.isFetching} />
  }
  const data = query.data
  const today = data.days.find((day) => day.day === data.to)
  return (
    <div className="flex flex-col gap-5">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-black text-ink">The AI briefings</h2>
          <HelpIcon topic="ai_briefings.usage" />
          <EndpointHint>GET /admin/ai</EndpointHint>
        </div>
        <p className="mt-1 text-xs text-ink-mid">
          {data.configured ? `Writing with ${data.model} at ${data.effort} effort` : 'No AI key is configured: the add-on is unoffered until one is set'}
          {' · '}
          {data.emailConfigured ? 'email is on' : 'no email sender, so briefings cannot be emailed'}
          {' · '}
          {data.dailyCap > 0 ? `${data.dailyCap.toLocaleString('en-US')} calls a day across the deployment` : 'no deployment cap'}
          {' · prices per million tokens: '}
          {data.prices.inputUsdPerMillion === null ? 'not set' : `$${data.prices.inputUsdPerMillion} in, $${data.prices.outputUsdPerMillion ?? '—'} out, $${data.prices.cacheReadUsdPerMillion ?? '—'} cached`}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label="Briefings today" value={String(today?.briefings ?? 0)} detail={`${today?.questions ?? 0} questions, ${today?.emails ?? 0} emails`} />
        <Tile label="Last seven days" value={String(data.totals.briefings)} detail={`${data.totals.questions} questions, ${data.totals.emails} emails`} />
        <Tile label="Tokens, seven days" value={(data.totals.inputTokens + data.totals.outputTokens).toLocaleString('en-US')} detail={`${data.totals.cacheReadTokens.toLocaleString('en-US')} read from the cache`} />
        <Tile label="Estimated cost, seven days" value={usdLabel(data.totals.costUsd)} detail={`${data.totals.failed} failed, ${data.totals.refused} refused, ${data.totals.empty} empty`} />
      </div>
      <div className="overflow-x-auto">
        <div className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-ink-mid">By day</div>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-ink-mid">
              <th className="py-1 pr-3 font-bold">Day</th>
              {HEADERS.map((header) => (
                <th key={header} className="py-1 pr-3 font-bold">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.days.length === 0 && (
              <tr>
                <td colSpan={HEADERS.length + 1} className="py-2 text-ink-mid">
                  Nothing in the last seven days.
                </td>
              </tr>
            )}
            {[...data.days].reverse().map((day) => (
              <tr key={day.day} className="border-t border-marine/10">
                <td className="py-1.5 pr-3 font-bold text-ink">{day.day}</td>
                <TotalsCells totals={{ ...day, costUsd: day.costMicroUsd / 1_000_000 }} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="overflow-x-auto">
        <div className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-ink-mid">By client, last seven days</div>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-ink-mid">
              <th className="py-1 pr-3 font-bold">Client</th>
              {HEADERS.map((header) => (
                <th key={header} className="py-1 pr-3 font-bold">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.byClient.length === 0 && (
              <tr>
                <td colSpan={HEADERS.length + 1} className="py-2 text-ink-mid">
                  No client has used the briefings this week.
                </td>
              </tr>
            )}
            {data.byClient.map((row) => (
              <tr key={row.clientId} className="border-t border-marine/10">
                <td className="py-1.5 pr-3 font-bold text-ink">
                  {row.organizationName} <span className="font-mono text-[10px] text-ink-mid">{row.clientId}</span>
                </td>
                <TotalsCells totals={row.totals} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
